/**
 * Client-side utility functions for customization admin.
 *
 * Mirrors server-side logic from customization_rules.py for
 * live preview during editing (server regenerates on save).
 *
 * Plan: 117-personalization-admin-ui, Epic 02
 */

import type { TemplateRule } from "./customizationAdminTypes";

/**
 * Generate a plain-language summary for a rule.
 * Mirrors `customization_rules.generate_rule_summary()` from Python.
 */
export function generateRuleSummary(rule: Partial<TemplateRule>): string {
  const when = rule.when_field || "?";
  const op = rule.operator || "equals";
  const hasVal = rule.has_value || "";
  const action = rule.then_action || "?";
  const target = rule.target_field || "?";
  const targetVal = rule.target_value || "";

  // Build condition phrase
  let condition: string;
  switch (op) {
    case "equals":
      condition = `If ${when} equals "${hasVal}"`;
      break;
    case "not_equals":
      condition = `If ${when} does not equal "${hasVal}"`;
      break;
    case "is_set":
      condition = `If ${when} is set`;
      break;
    case "is_empty":
      condition = `If ${when} is empty`;
      break;
    case "contains":
      condition = `If ${when} contains "${hasVal}"`;
      break;
    default:
      condition = `If ${when} ${op} ${hasVal}`;
  }

  // Build action phrase
  const actionMap: Record<string, string> = {
    "Show Field": `show ${target}`,
    "Hide Field": `hide ${target}`,
    "Set Value": `set ${target} to "${targetVal}"`,
    "Force Value": `force ${target} to "${targetVal}"`,
    "Set Required": `make ${target} required`,
    "Clear Required": `make ${target} optional`,
  };
  const actionPhrase = actionMap[action] || `${action} ${target}`;

  return `${condition}, ${actionPhrase}`;
}
